import {
    inject,
    Injectable
} from "@angular/core";

import {PrepaidMovementsFields} from "../../../../features/customers/customers.model";
import {KeyValue} from "@angular/common";
import { TranslateService } from "@ngx-translate/core";
import {TilbyDatePipe} from "@tilby/tilby-ui-lib/pipes/tilby-date";
import { TilbyCurrencyPipe } from "@tilby/tilby-ui-lib/pipes/tilby-currency";
import { Sales, Vat } from "tilby-models";
import { ItemDataCatalogService } from "src/app/features/items/services/item-data-catalog.service";

@Injectable()
export class GridCellFormatterService {
    private readonly tilbyDatePipe = inject(TilbyDatePipe);
    private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);
    private readonly translateService = inject(TranslateService);
    private readonly itemDataCatalogService = inject(ItemDataCatalogService);
    

    tilbyCurrency = (params: KeyValue<string, any>) => {
        return params.value !== null && params.value !== undefined? this.tilbyCurrencyPipe.transform(params.value): '';
    }

    sclAmountTransformAndCurrency = (params:KeyValue<string, any>&{data:PrepaidMovementsFields}) => {
        if (!params?.data) {
            return ''; 
        }
        const {amount, ticket_amount} = params.data;
        return this.tilbyCurrencyPipe.transform(amount||ticket_amount);
    }
    sclCreditTransformAndCurrency = (params:KeyValue<string, any>&{data:PrepaidMovementsFields}) => {
        if (!params?.data) {
            return ''; 
        }
        const {credit, ticket_credit} = params.data;
        return this.tilbyCurrencyPipe.transform(credit+ticket_credit);
    }

    dateTimeFormat = (params:KeyValue<string, any>) => {
        return this.tilbyDatePipe.transform(params.value);
    }

    dateFormat = (params:KeyValue<string, any>) => {
        return this.tilbyDatePipe.transform(params.value, 'dd/MM/yyyy');
    }

    retryFormat = (params: KeyValue<string, any>) => {
        return params.value != undefined && Number(params.value) >= 0 ? Number(params.value) + 1: 0;
    }

    percentuageFormat = (params:KeyValue<string, any>) => {
        return params.value ? params.value + " %" : null;
    }

    // FIX : add shop_name in table prepaid_movements
    shopFormat = (params:KeyValue<string, any>) => {
        return params.value;
    }

    typeDocumentFormat = (params:KeyValue<string, any>) => {
        return params.value ? this.translateService.instant("HISTORY.DOCUMENTS_VIEWER." + params.value.toUpperCase()) : '';
    }

    boolanToYesNoFormat = (params: KeyValue<string, any>) => {
        let checkType = typeof params.value === 'boolean' ? params.value == true : params.value == 'true';
        return checkType ? this.translateService.instant("APPLICATION.YES") : this.translateService.instant("APPLICATION.NO");
    }

    tokenPermission = (params: KeyValue<string, any>) => {
        let translation: string =
        params.value === 'SclStaticRO' ? 'SETTINGS.USERS_MANAGEMENT.PERMISSIONS.SCL_STATIC_RO.TITLE' :
        params.value === 'SclStaticRW' ? 'SETTINGS.USERS_MANAGEMENT.PERMISSIONS.SCL_STATIC_RW.TITLE' :
        params.value === 'SclStaticECom' ? 'SETTINGS.USERS_MANAGEMENT.PERMISSIONS.SCL_STATIC_ECOM.TITLE' :
        params.value === 'SclStaticBookings' ? 'SETTINGS.USERS_MANAGEMENT.PERMISSIONS.SCL_STATIC_BOOKINGS.TITLE' :
        params.value === 'SclStaticStock' ? 'SETTINGS.USERS_MANAGEMENT.PERMISSIONS.SCL_STATIC_STOCK.TITLE' :
        params.value === 'SclStaticCustomers' ? 'SETTINGS.USERS_MANAGEMENT.PERMISSIONS.SCL_STATIC_CUSTOMERS.TITLE' :
        params.value === 'SclStaticStockCustomers' ? 'SETTINGS.USERS_MANAGEMENT.PERMISSIONS.SCL_STATIC_STOCK_CUSTOMERS.TITLE' :
        'SETTINGS.USERS_MANAGEMENT.PERMISSIONS.CUSTOM.TITLE';
        return this.translateService.instant(translation);
    }

    getCashMovementType = (movement_type: {value: string}) =>{
        let translation: string = movement_type.value === 'income' ? 'HISTORY.CASH_MOVEMENTS.INCOME' : 'HISTORY.CASH_MOVEMENTS.OUTCOME';

        return this.translateService.instant(translation);
    };

    getTopBarCashMovements = (movement_type: {value: string}) =>{
        let translation: string = movement_type.value === 'cash' ? 'HISTORY.TOPBAR_CASH_MOVEMENTS.CASHREGISTER' : 'HISTORY.TOPBAR_CASH_MOVEMENTS.OUT_CASHREGISTER';

        return this.translateService.instant(translation);
    }

    getStatusFileImported = (file_imported_status: {value: string}) => {
        return this.translateService.instant(`FILE_IMPORTER.FILTERS.${file_imported_status?.value?.toUpperCase()}`);
    }

    getTypeFileImported = (file_imported_type: {value: string}) => {
        return this.translateService.instant(`FILE_IMPORTER.IMPORT_TYPES.${file_imported_type?.value?.toUpperCase()}`);
    }

    getTable = (file_exported_table: {value: string}) => {
        return this.translateService.instant(`FILE_EXPORTER.TABLES.${file_exported_table?.value?.toUpperCase()}`);
    }

    getStockType = (stock_type: {value: string}) =>{
        return stock_type.value === 'bom' ? this.translateService.instant('ITEMS.SHOWCASE.STOCK_TYPE_BOM') :
            stock_type.value === 'simple' ? this.translateService.instant('ITEMS.SHOWCASE.STOCK_TYPE_SIMPLE') : '';
    }

    getEnabledStockType = (stock_type: {value: string}) =>{
        return stock_type.value === 'simple' ? this.translateService.instant('APPLICATION.YES') : this.translateService.instant('APPLICATION.NO');
    }

    getObjectValue = (obj: {value: Vat}) =>{
        return obj.value? obj.value.value? obj.value.value + '%':
            obj.value.code? obj.value.code : '': '';
    }

    getSalesType = (sales_type: {value: string}) =>{
        return sales_type.value === 'goods' ? this.translateService.instant('ITEMS.DEPARTMENTS.SALES_TYPES.GOODS') :
            sales_type.value === 'services' ? this.translateService.instant('ITEMS.DEPARTMENTS.SALES_TYPES.SERVICES') : '';
    }

    getStatusSaleDataSent = (status: {value: string}) =>{
        return status.value === 'closed' ? this.translateService.instant('HISTORY.SALES_DATA_SENT.SALE_STATUS.CLOSED') :
            status.value === 'pending' ? this.translateService.instant('HISTORY.SALES_DATA_SENT.SALE_STATUS.PENDING') :
            status.value === 'failed' ? this.translateService.instant('HISTORY.SALES_DATA_SENT.SALE_STATUS.FAILED') : '';
    }

    getNameSupplier = (supplier: {value: string}) => {
        const id = supplier?.value;
        if (!id) return '';

        // Attenzione: la lista è valorizzata solo se ci si trova dentro il modulo prodotti
        const supplierList = this.itemDataCatalogService.getDataByKey('suppliers') ?? [];
        const foundSupplier = supplierList.find(s => s.id === parseInt(id));

        return foundSupplier ? foundSupplier.name : id;
    }

    getGiftcardType = (giftcard_type_uuid: {value: string}) => {
        const uuid = giftcard_type_uuid?.value;
        if (!uuid) return '';

        // Attenzione: la lista è valorizzata solo se ci si trova dentro il modulo prodotti
        const giftcardList = this.itemDataCatalogService.getDataByKey('giftcards-types') ?? [];
        const foundGiftcard = giftcardList.find(s => s.uuid === uuid);

        return foundGiftcard ? foundGiftcard.name : uuid;
    }

    getDepartmentName = (params: any) => {
        // console.log("getDepartmentName ", params);
        const id = params.value; 
        if (!id) return '';
        if (typeof id === 'object') return id.name || '';
        const departments = this.itemDataCatalogService.getDataByKey('departments') ?? [];
        const found = departments.find(d => d.id === parseInt(id));
        return found ? found.name : id;
    }

    getCategoryName = (params: any) => {
        const id = params.value;
        if (!id) return '';
        if (typeof id === 'object') return id.name || '';
        const categories = this.itemDataCatalogService.getDataByKey('categories') ?? [];
        const found = categories.find(c => c.id === parseInt(id));
        return found ? found.name : id;
    }

    getAvailability = (available: {value: string}) => {
        switch(available.value) {
            case 'available':
            case 'producible':
                return this.translateService.instant('STOCK.MANAGEMENT.AVAILABLE');
            case 'unavailable':
                return this.translateService.instant('STOCK.MANAGEMENT.NOT_AVAILABLE');
            case 'alert':
                return this.translateService.instant('STOCK.MANAGEMENT.LOW_STOCK');
        }
    }

    getCustomerName = (params: { data: Sales }): string => {
        const sale = params.data;
        if (!sale) return '';

        // const invoiceNum = sale.e_invoice?.invoice_yearly_progressive ?? 'N/A';
        // const invoicePart = `Fattura n. ${invoiceNum}`;

        let customerPart = '';
        if (sale.customer_tax_code) {
            customerPart = sale.customer_tax_code;
        } else {
            customerPart = this.getCustomerCaption(sale.sale_customer) || '';
        }

        return `${customerPart}`;
    }

    private getCustomerCaption = (customer: any): string | null => {
        if (!customer || typeof customer !== 'object') return null;

        if (customer.company_name) {
            return customer.company_name;
        } else if (customer.first_name && customer.last_name) {
            const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
            return `${capitalize(customer.first_name)} ${capitalize(customer.last_name)}`;
        }
        return null;
    }

    getStatusEInvoice = (params: { data: Sales }): string => {
        const eInvoice = params.data?.e_invoice;
        if (!eInvoice) return '';

        const status = eInvoice.status === 'pending' ? this.translateService.instant('HISTORY.E_INVOICE.SINGLE_STATUS.PENDING') :
            eInvoice.status === 'sent' ? this.translateService.instant('HISTORY.E_INVOICE.SINGLE_STATUS.SENT') :
            eInvoice.status === 'delivered' ? this.translateService.instant('HISTORY.E_INVOICE.SINGLE_STATUS.DELIVERED') :
            eInvoice.status === 'error' ? this.translateService.instant('HISTORY.E_INVOICE.SINGLE_STATUS.ERROR') :
            eInvoice.status === 'downloaded' ? this.translateService.instant('HISTORY.E_INVOICE.SINGLE_STATUS.DOWNLOADED') : '';

        const external_status = (() => {
            switch(params.data?.e_invoice?.external_status) {
                //Digithera
                case "IN_PROGRESS": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.IN_PROGRESS');
                case "USER_VERIFY": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.USER_VERIFY');
                case "VERIFY": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.VERIFY');
                case "IS_READY_FOR_SIGNATURE": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.IS_READY_FOR_SIGNATURE');
                case "DONE": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.DONE');
                case "IS_CREATED": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.IS_CREATED');
                case "SKIPPED": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.SKIPPED');
                case "IS_CANCELLED": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.IS_CANCELLED');
                case "IS_NOT_VALID": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.IS_NOT_VALID');
                case "IS_DUPLICATED": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.IS_DUPLICATED');
                case "IS_NOT_DELIVERED": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.IS_NOT_DELIVERED');
                case "IS_UNDELIVERABLE": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.IS_UNDELIVERABLE');
                case "IS_REPLY_OK": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.IS_REPLY_OK');
                case "IS_REPLY_KO": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.IS_REPLY_KO');
                case "IS_REPLY_TIMEOUT": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.IS_REPLY_TIMEOUT');
                //Digital Hub
                case "DF": return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.DF');
                case 'DI': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.DI');
                case 'DV': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.DV');
                case 'DC': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.DC');
                case 'IE': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.IE');
                case 'IC': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.IC');
                case 'MC': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.MC');
                case 'CO': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.CO');
                case 'RI': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.RI');
                case 'SC': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.SC');
                case 'NR': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.NR');
                case 'RC': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.RC');
                case 'AC': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.AC');
                case 'DT': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.DT');
                case 'NT': return this.translateService.instant('HISTORY.E_INVOICE.EXTERNAL_STATUS.NT');
            }
        })();
        const final_status = external_status? `${status}\n${external_status}`: status;

        return final_status;
    }

    getEInvoiceNumber = (params: { data: Sales }): string => {
        const sale = params.data;
        if (!sale?.sale_documents?.length) return '';

        const sequential_number_prefix = sale.sale_documents[0].sequential_number_prefix || '';
        const sequential_number = sale.sale_documents[0].sequential_number || '';

        return `${sequential_number_prefix}${sequential_number}`;
    }

    suppliers = new Map<string, Function>([
        ["created_at", this.dateTimeFormat],
        ["updated_at", this.dateTimeFormat],
    ]);

    customers_prepaid = new Map<string, Function>([
        ["valid_from", this.dateTimeFormat],
        ["amount", this.sclAmountTransformAndCurrency],
        ["credit",this.sclCreditTransformAndCurrency],
        ["shop_uuid", this.shopFormat]
    ]);

    customers_fidelity = new Map<string, Function>([
        ["date", this.dateTimeFormat]
    ]);

    customers = new Map<string, Function>([
        ["tot_spent", this.tilbyCurrency],
        ["avg_spent",this.tilbyCurrency],
        ["credit",this.tilbyCurrency],
        ["discount_perc",this.percentuageFormat],
        ["birthdate",this.dateFormat],
        ["created_at", this.dateTimeFormat],
        ["updated_at", this.dateTimeFormat]
    ]);

    history_sales = new Map<string, Function>([
        ["closed_at",this.dateTimeFormat],
        ["open_at",this.dateTimeFormat],
        ["final_amount",this.tilbyCurrency],
        ["change",this.tilbyCurrency],
        ["type_document", this.typeDocumentFormat],
        ['is_summary', this.boolanToYesNoFormat],
        ['summary_emitted', this.boolanToYesNoFormat],
    ]);

    history_sales_data_sent = new Map<string, Function>([
        ["final_amount",this.tilbyCurrency],
        ["closed_at",this.dateTimeFormat],
        ["sale_data_sent.0.status",this.getStatusSaleDataSent],
        ["sale_data_sent.0.created_at",this.dateTimeFormat],
        ["sale_data_sent.0.updated_at",this.dateTimeFormat],
        ["sale_data_sent.0.retry",this.retryFormat]
    ]);

    history_e_invoices = new Map<string, Function>([
        ["customer_name", this.getCustomerName],
        ["final_amount", this.tilbyCurrency],
        ["closed_at", this.dateTimeFormat],
        ["status", this.getStatusEInvoice],
        ["e_invoice_number", this.getEInvoiceNumber]
    ]);

    giftcards = new Map<string, Function>([
        ['active', this.boolanToYesNoFormat],
        ['created_at', this.dateTimeFormat],
        ['deleted_at', this.dateTimeFormat],
        ["initial_value", this.tilbyCurrency],
        ['is_divisible', this.boolanToYesNoFormat],
        ["last_value", this.tilbyCurrency],
        ['updated_at', this.dateTimeFormat],
        ['valid_since', this.dateTimeFormat],
        ['valid_until', this.dateTimeFormat]
    ]);

    token_static = new Map<string, Function>([
        ["client_id",this.tokenPermission],
        ["created_at", this.dateTimeFormat],
    ]);

    history_cash_movements = new Map<string, Function>([
        ["date",this.dateTimeFormat],
        ["amount",this.tilbyCurrency],
        ["movement_type",this.getCashMovementType],
        ["movement_account",this.getTopBarCashMovements]
    ]);
    file_imported = new Map<string, Function>([
        ['status', this.getStatusFileImported],
        ['dateToProcess', this.dateTimeFormat],
        ['typeInsert', this.getTypeFileImported],
    ]);
    file_exported = new Map<string, Function>([
        ['status', this.getStatusFileImported],
        ['created_at', this.dateTimeFormat],
        ['table', this.getTable]
    ]);

    getCustomerTypePromotions = (customer_type: {value: string}) => {
        return customer_type.value === '' ? this.translateService.instant('PROMOTIONS.DETAILS.NONE') : customer_type.value;
    };
    promotions = new Map<string, Function>([
        ["created_at", this.dateTimeFormat],
        ["updated_at", this.dateTimeFormat],
        ['deleted_at', this.dateTimeFormat],
        ['customer_type', this.getCustomerTypePromotions],
    ]);

    history_products_deleted = new Map<string, Function>([
        ["sale_item_deleted_at", this.dateTimeFormat],
        ["lastupdate_at", this.dateTimeFormat],
        ["vat_perc", this.percentuageFormat],
        ["price", this.tilbyCurrency],
        ["cost", this.tilbyCurrency],
        ["revenues", this.tilbyCurrency]
    ]);

    items = new Map<string, Function>([
        ["created_at", this.dateTimeFormat],
        ["updated_at", this.dateTimeFormat],
        ["price1", this.tilbyCurrency],
        ["price2", this.tilbyCurrency],
        ["price3", this.tilbyCurrency],
        ["price4", this.tilbyCurrency],
        ["price5", this.tilbyCurrency],
        ["price6", this.tilbyCurrency],
        ["price7", this.tilbyCurrency],
        ["price8", this.tilbyCurrency],
        ["price9", this.tilbyCurrency],
        ["price10", this.tilbyCurrency],
        ["cost", this.tilbyCurrency],
        ["not_discountable", this.boolanToYesNoFormat],
        ["request_weighing", this.boolanToYesNoFormat],
        ["is_frozen", this.boolanToYesNoFormat],
        ["stock_type", this.getStockType],
        ["auto_unload", this.boolanToYesNoFormat],
        ["manual_production", this.boolanToYesNoFormat],
        ["on_sale", this.boolanToYesNoFormat],
        ["is_group_item", this.boolanToYesNoFormat],
        ["split_group_components", this.boolanToYesNoFormat],
        ["favorite", this.boolanToYesNoFormat],
        ["checkout_mark", this.boolanToYesNoFormat],
        ["default_supplier_id", this.getNameSupplier],
        ["category_name", this.getCategoryName],
        ["department_name", this.getDepartmentName],
        ["department2_name", this.getDepartmentName],
        ["department3_name", this.getDepartmentName],
        ["department4_name", this.getDepartmentName],
        ["department5_name", this.getDepartmentName],
        ["department6_name", this.getDepartmentName],
        ["department7_name", this.getDepartmentName],
        ["department8_name", this.getDepartmentName],
        ["department9_name", this.getDepartmentName],
        ["department10_name", this.getDepartmentName],
    ])

    categories = new Map<string, Function>([
        ["display", this.boolanToYesNoFormat],
        ["display_kiosk", this.boolanToYesNoFormat],
        // ["display_website", this.boolanToYesNoFormat],
        ["display_slave_device", this.boolanToYesNoFormat]
    ])

    components = new Map<string, Function>([
        ["price_difference", this.tilbyCurrency]
    ])

    raw_materials = new Map<string, Function>([
        ["cost", this.tilbyCurrency],
        ["stock_type", this.getEnabledStockType],
        ["auto_unload", this.boolanToYesNoFormat],
        ["default_supplier_id", this.getNameSupplier],
    ])

    departments = new Map<string, Function>([
        ["display", this.boolanToYesNoFormat],
        ["not_discountable", this.boolanToYesNoFormat],
        ["vat", this.getObjectValue],
        ["sales_type", this.getSalesType],
        ["giftcard_type_uuid", this.getGiftcardType],
    ])

    stock = new Map<string, Function>([
        ['available', this.getAvailability],
        ["item_on_sale", this.boolanToYesNoFormat],
        ["lastupdate_at", this.dateTimeFormat]
    ])

    private quantityWithUnit = (params: any) => {
        return params.value == null ? '' : `${params.value} ${params.data?.unit ?? ''}`.trim();
    }

    stock_movements = new Map<string, Function>([
        ["date", this.dateTimeFormat]
    ])

    private nameWithSku = (params: any) => {
        const sku = params.data?.sku;
        return sku ? `${params.value ?? ''} (${sku})` : (params.value ?? '');
    }

    stock_movements_search = new Map<string, Function>([
        ["name", this.nameWithSku],
        ["quantity", this.quantityWithUnit],
        ["price", this.tilbyCurrency],
        ["perc_discount", this.percentuageFormat],
        ["row_amount", this.tilbyCurrency],
        ["date", this.dateTimeFormat]
    ])

    stock_movements_detail = new Map<string, Function>([
        ["quantity", this.quantityWithUnit],
        ["price", this.tilbyCurrency],
        ["perc_discount", this.percentuageFormat],
        ["row_amount", this.tilbyCurrency]
    ])
}
